import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Constants from "../Constants";
import useSortableData from "../components/customHook/SortableTable";
import CaseListTable from "../components/case-list/CaseListTable";
import { useProductContext } from "../contexts/ProductContext";
import usePaginatedList from "./usePaginatedList";
import useUserSetting from "./useUserSetting";
import usePolling from "./usePolling";
import useUser from "./useUser";
import { CREATE_REQUEST_STATUS, REQUEST_TYPES } from "@demandproai/shared";

const POLLING_INTERVAL = 60 * 1000; // 1 minute

const SETTLED_REQUEST_STATUSES = [
   CREATE_REQUEST_STATUS.COMPLETED,
   CREATE_REQUEST_STATUS.FAILED,
   CREATE_REQUEST_STATUS.BAD_RECORD
];

// An Easy Mode case stays a draft until its chained demand starts, so the demand progress the check
// below reads does not exist yet. Nothing is pushed to the browser, so without this the row sits still
// through the whole agent run and only catches up on a manual reload.
const getHasVirtualUserRunning = (caseItem) => {
   const status = caseItem?.createRequests?.[REQUEST_TYPES.VIRTUAL_USER]?.status;

   return Boolean(status) && !SETTLED_REQUEST_STATUSES.includes(status);
};

/**
 * Determines if a case has any in-progress work across all processing stages.
 * @param {Object} caseItem - Case object with resultProgress field
 * @returns {boolean} True if a draft case has a live virtual-user run, or if any of the five progress fields is neither Successful nor Failed
 */
const isCaseRunning = (caseItem) => {
   if (!caseItem) return false;
   if (caseItem.isDraftCase) return getHasVirtualUserRunning(caseItem);
   const progress = caseItem?.resultProgress;
   const progressFields = [
      progress?.policeReport_Progress,
      progress?.medicalRecords_progress,
      progress?.preMedicalRecords_progress,
      progress?.medicalBills_progress,
      progress?.demandLetter_progress
   ];
   return progressFields.some(field => field !== "Successful" && field !== "Failed");
};

const useCaseList = () => {
   const { fetchAndStoreUser } = useUser(false)
   const [searchParams] = useSearchParams();
   const urlSearch = searchParams.get("search") || "";

   // A parent app can supply a resolver that picks its own table, or opts out by returning null
   const { getOverrideCaseListTable } = useProductContext();

   const getCaseListTableOverride = useCallback(() => {
      if (!getOverrideCaseListTable) {
         return CaseListTable;
      }
      const parentCaseListTable = getOverrideCaseListTable();
      if (parentCaseListTable) {
         return parentCaseListTable;
      }
      return CaseListTable;
   }, [getOverrideCaseListTable]);

   const [isMyCases, setIsMyCases] = useUserSetting({
      key: "isMyCases",
      defaultValue: false,
   });
   const [persistedPage, setPersistedPage] = useUserSetting({
      key: "caseListPage",
      defaultValue: 1,
   });

   const extraParams = useMemo(() => ({ isMyCases }), [isMyCases]);

   const {
      items: casesList,
      isLoading: isCaseListLoading,
      currentPage,
      limit,
      totalCount,
      onSearchInputChange,
      setSearch,
      setCurrentPage,
      setLimit,
      refetch,
   } = usePaginatedList({
      endpoint: Constants.ApiUrl.case.getCases,
      extraParams,
      initialSearch: urlSearch,
      initialPage: Number(persistedPage) > 0 ? Number(persistedPage) : 1,
      selectItems: (data) => data.cases,
      onSuccess : fetchAndStoreUser,
      onPageChange: setPersistedPage,
   });

   const { items } = useSortableData(casesList);

   // Notification clicks navigate to /cases?search=<caseId> while CaseList is already mounted
   useEffect(() => {
      setSearch(urlSearch);
   }, [urlSearch, setSearch]);

   // the notification event only fires when a demand completes, so a virtual-user run has nothing
   // else refreshing its row for the length of the agent
   const hasRunningCase = useMemo(() => casesList.some(isCaseRunning), [casesList]);
   usePolling({
      intervalMs: POLLING_INTERVAL,
      callback: () => refetch({ showLoader: false }),
      enabled: hasRunningCase,
   });

   return {
      items,
      currentPage,
      totalCount,
      limit,
      isMyCases,
      isCaseListLoading,
      setCurrentPage,
      onSearchInputChange,
      setIsMyCases,
      setLimit,
      getCases: refetch,
      getCaseListTableOverride,
   };
};

export default useCaseList;
